// PremiumLogin.js
import React, { useState } from 'react';
import {
  Box,
  TextField,
  Button,
  Typography,
  Paper,
  InputAdornment,
  IconButton,
  CircularProgress,
  Avatar,
  Divider,
  Checkbox,
  FormControlLabel,
  Link,
  Alert,
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { GoogleLogin } from '@react-oauth/google';
import { jwtDecode } from 'jwt-decode';
import authService from '../Auth/AuthService';
import { useNavigate } from 'react-router-dom';

const EMPTY_LOGIN = { username: '', password: '' };
const EMPTY_REGISTER = { email: '', mobile: '', password: '', confirmPassword: '' };

const Login = () => {
  const [mode, setMode] = useState('login'); // 'login' | 'register'
  const [error, setError] = useState('');

  // --- Login state ---
  const [loginData, setLoginData] = useState(EMPTY_LOGIN);
  const [showPassword, setShowPassword] = useState(false);
  // const [checkingEmail, setCheckingEmail] = useState(false);
  const [emailChecked, setEmailChecked] = useState(false);
  const [emailStatus, setEmailStatus] = useState(null); // true / false / null
  // const [isDisabled, setIsDisabled] = useState(false);

  // --- Register state ---
  const [registerData, setRegisterData] = useState(EMPTY_REGISTER);
  const [showRegisterPassword, setShowRegisterPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [manualSignup, setManualSignup] = useState(false);
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [registering, setRegistering] = useState(false);
  // const [emailStatus, setEmailStatus] = useState(null); // { googleUser, normalUser } | null
  const [showContinueButton, setShowContinueButton] = useState(true);
  const [loading, setLoading] = useState(false);

  const navigate = useNavigate();
  const name = localStorage.getItem('name');
  const logoUrl = localStorage.getItem('logoUrl');

  const goToLogin = () => {
    setMode('login');
    setError('');
    setLoginData(EMPTY_LOGIN);
    setEmailChecked(false);
    setEmailStatus(null);
  };

  const goToRegister = () => {
    setMode('register');
    setError('');
    setRegisterData(EMPTY_REGISTER);
    setAcceptTerms(false);
    setManualSignup(false);
  };

  const handleLoginChange = (e) => {
    const { name, value } = e.target;
    setLoginData((prev) => ({ ...prev, [name]: value }));
    if (name === 'username' && emailStatus !== null) {
      setEmailChecked(false);
      setEmailStatus(null);
      setShowContinueButton(true);
    }
  };

  const handleRegisterChange = (e) => {
    setRegisterData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
    setError('');
  };

  const routeAfterLogin = (tokendata) => {
    if (tokendata?.is_verfied === true || tokendata?.roles === 'SUPPERADMIN') {
      navigate('/');
    } else if (tokendata?.is_verfied === false) {
      navigate('/verify');
    } else {
      navigate('/loginview');
    }
    window.location.reload();
  };

  // --- Step 1: check if this email/username exists ---
  const handleCheckEmail = async () => {
    if (!loginData.username.trim()) {
      setError('Please enter your email or username');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const rest = authService.pathUrl('POST', '/auth/check-email');
      const res = await authService.restPost(rest, { username: loginData.username });

      if (!res || (!res.googleUser && !res.normalUser)) {
        setEmailStatus({ googleUser: false, normalUser: false });
        setError('No account found with this email. Try registering, or use Google.');
        setMode('register');
        return;
      }

      setEmailStatus({ googleUser: !!res.googleUser, normalUser: !!res.normalUser });
      setShowContinueButton(false);

      if (res.googleUser) {
        setError('This email is registered with Google. Please sign in with Google.');
        // sessionStorage.setItem('token', res.accessToken);
        // routeAfterLogin(authService.tokenInfo())
      } else if (res.normalUser) {
        setError(''); // clear — password field will just appear
      }
    } catch (err) {
      console.error('Email check failed:', err);
      setError('Unable to verify email. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const resetEmailCheck = () => {
    setError('');
    setEmailStatus(null);
    setShowContinueButton(true);
    setAcceptTerms(false);
    setManualSignup(false);
    setLoading(false);
    setLoginData((prev) => ({ ...prev, password: '' }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const rest = authService.pathUrl('POST', '/auth/login');
      const tokendata = await authService.registerLogin(rest, loginData);
      routeAfterLogin(tokendata);
    } catch (err) {
      setError(err.message || 'Login failed. Please try again.');
      setTimeout(() => setLoading(false), 7000);
    }
  };

  // --- Google login (existing account) ---
  const handleGoogleLogin = async (credentialResponse) => {
    setError('');
    try {
      const decoded = jwtDecode(credentialResponse.credential);
      const rest = authService.pathUrl('POST', '/auth/google-login');
      const tokendata = await authService.registerLogin(rest, {
        username: decoded.email,
      });
      routeAfterLogin(tokendata);
    } catch (err) {
      console.error('Google login error:', err);
      setError('Google login failed. Please try again.');
    }
  };

  // --- Google signup (new account, needs mobile + terms) ---
  const handleGoogleSignup = async (credentialResponse) => {
    if (!acceptTerms) {
      setError('Please accept the Terms & Conditions to continue.');
      return;
    }
    if (!/^\d{10}$/.test(registerData.mobile)) {
      setError('Please enter a valid 10-digit mobile number.');
      return;
    }

    setError('');
    try {
      const decoded = jwtDecode(credentialResponse.credential);
      const rest = authService.pathUrl('POST', '/auth/google-signup');
      const tokendata = await authService.registerLogin(rest, {
        idToken: credentialResponse.credential,
        email: decoded.email,
        name: decoded.name,
        mobile: registerData.mobile,
        acceptTerms: true,
      });
      routeAfterLogin(tokendata);
    } catch (err) {
      console.error('Google signup error:', err);
      setError('Google signup failed. Please try again.');
    }
  };

  const handleGoogleError = () => {
    setError('Google authentication failed. Please try again.');
  };

  // --- Manual registration (email + password) ---
  const validateManualRegister = () => {
    if (!registerData.email.trim()) return 'Email is required';
    if (!/^\S+@\S+\.\S+$/.test(registerData.email)) return 'Invalid email format';
    if (!registerData.mobile.trim()) return 'Mobile number is required';
    if (!/^\d{10}$/.test(registerData.mobile)) return 'Mobile must be 10 digits';
    if (!registerData.password) return 'Password is required';
    if (registerData.password.length < 6) return 'Password must be at least 6 characters';
    if (registerData.password !== registerData.confirmPassword) return 'Passwords do not match';
    if (!acceptTerms) return 'You must accept the Terms and Conditions to register';
    return null;
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    const validationError = validateManualRegister();
    if (validationError) {
      setError(validationError);
      return;
    }

    setRegistering(true);
    setError('');
    try {
      const rest = authService.pathUrl('POST', '/auth/register');
      const tokendata = await authService.registerLogin(rest, {
        email: registerData.email,
        mobile: registerData.mobile,
        password: registerData.password,
        acceptTerms: true,
      });
      routeAfterLogin(tokendata);
    } catch (err) {
      setError(err.message || 'Registration failed. Please try again.');
      // } finally {
      //   setRegistering(false);
    }
  };

  // --- Light-theme input styling ---
  const inputSx = {
    borderRadius: 2,
    '& .MuiInputBase-input': {
      backgroundColor: '#ffffff',
      color: '#1a1a1a',
    },
    '& .MuiInputBase-input:-webkit-autofill': {
      WebkitBoxShadow: '0 0 0 1000px #ffffff inset !important',
      WebkitTextFillColor: '#1a1a1a !important',
    },
    '& .MuiOutlinedInput-root': {
      '& fieldset': { borderColor: '#d0d5dd' },
      '&:hover fieldset': { borderColor: '#98a2b3' },
      '&.Mui-focused fieldset': { borderColor: '#1976d2', borderWidth: '1px' },
    },
  };

  const primaryButtonSx = {
    mt: 3,
    bgcolor: '#1976d2',
    fontWeight: 'bold',
    fontSize: '16px',
    paddingY: 1,
    borderRadius: 6,
    '&:hover': { backgroundColor: '#125ea8' },
  };

  const handleTermsChange = (e) => {
    setAcceptTerms(e.target.checked);
    setError('');
  };

  const termsCheckbox = (
    <FormControlLabel
      control={<Checkbox checked={acceptTerms} onChange={handleTermsChange} color="primary" />}
      label={
        <Typography variant="body2">
          I agree to the{' '}
          <Link href="/terms-conditions" target="_blank" rel="noopener" underline="hover" color="primary">
            Terms and Conditions
          </Link>
        </Typography>
      }
    />
  );

  const renderGoogleSignup = () => {
    const ready = /^\d{10}$/.test(registerData.mobile) && acceptTerms;
    if (!ready) {
      return (
        <Typography variant="body2" fullWidth sx={{ borderRadius: 6, border: '1px solid lightgray', color: '#f8b5b5' }}>
          Enter valid 10-digit mobile number & accept terms to use Google
        </Typography>
      );
    }
    return (
      <Box display="flex" justifyContent="center">
        <GoogleLogin onSuccess={handleGoogleSignup} onError={handleGoogleError} theme="outline" shape="pill" width="200" text="signup_with" />
      </Box>
    );
  };

  return (
    <Box
      sx={{
        height: '100vh',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        bgcolor: '#f5f6f8',
      }}
    >
      <Paper
        elevation={4}
        sx={{
          p: 5,
          width: 400,
          color: '#1a1a1a',
          borderRadius: 5,
          textAlign: 'center',
          backgroundColor: '#ffffff',
          border: '1px solid #e4e7ec',
        }}
      >
        {logoUrl !== 'undefined' && (
          <Box display="flex" justifyContent="center" sx={{ mt: -10 }}>
            <Avatar src={logoUrl} alt="School Logo" sx={{ width: 80, height: 80, border: '3px solid #ffffff', boxShadow: 1 }} />
          </Box>
        )}

        <Typography variant="h5" fontWeight={600} gutterBottom sx={{ fontFamily: 'monospace' }}>
          {name !== 'undefined' ? name : 'JMK'}
        </Typography>
        <Typography variant="subtitle1" mb={3} sx={{ color: '#667085' }}>
          {mode === 'login' ? 'Welcome Back | Sign in to continue' : 'Create your account'}
        </Typography>

        {error && <Alert severity="error" sx={{ mb: 2, textAlign: 'left' }}>{error}</Alert>}

        {/* ---------------- LOGIN ---------------- */}
        {mode === 'login' && (
          <>
            <form onSubmit={handleSubmit}>
              <TextField
                label="User Name / Email"
                name="username"
                fullWidth
                variant="outlined"
                value={loginData.username}
                onChange={handleLoginChange}
                margin="normal"
                disabled={emailChecked}
                sx={inputSx}
              />

              <TextField
                label="Password"
                name="password"
                fullWidth
                variant="outlined"
                type={showPassword ? 'text' : 'password'}
                value={loginData.password}
                onChange={handleLoginChange}
                margin="normal"
                autoFocus
                slotProps={{
                  input: {
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton onClick={() => setShowPassword(!showPassword)}>
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  },
                }}
                sx={inputSx}
              />

              {showContinueButton && (
                <Button type="submit" fullWidth variant="contained" disabled={loading || !loginData.username} sx={primaryButtonSx}>
                  {loading ? (
                    <CircularProgress size={20} sx={{ color: '#fff' }} />
                  ) : (
                    'Login'
                  )}
                </Button>
              )}
            </form>

            <Box mt={2}>
              <Typography variant="body2" sx={{ mb: 2, fontWeight: 900 }}>
                OR
              </Typography>
              <Box display="flex" justifyContent="center">
                <GoogleLogin onSuccess={handleGoogleLogin} onError={handleGoogleError} theme="outline" shape="pill" width="200" text="signin_with" />
              </Box>
            </Box>


            {/* {emailStatus?.normalUser && (
              <>
                <Divider sx={{ my: 3, color: '#98a2b3', fontSize: 12 }}>OR</Divider>
                <Box display="flex" justifyContent="center">
                  <GoogleLogin onSuccess={handleGoogleLogin} onError={handleGoogleError} theme="outline" shape="pill" width="200" text="signin_with" />
                </Box>
              </>
            )} */}

            {emailChecked && (
              <Typography variant="body2" mt={2} sx={{ cursor: 'pointer', textDecoration: 'underline', color: '#475467' }} onClick={resetEmailCheck}>
                Use a different email
              </Typography>
            )}

            <Typography variant="body2" mt={3} sx={{ color: '#475467' }}>
              Don't have an account?{' '}
              <Link component="button" type="button" onClick={goToRegister}>
                Register Now
              </Link>
            </Typography>
          </>
        )}

        {/* ---------------- REGISTER ---------------- */}
        {mode === 'register' && (
          <>
            <form onSubmit={handleRegister}>
              <TextField
                label="Mobile Number"
                name="mobile"
                fullWidth
                variant="outlined"
                type="tel"
                value={registerData.mobile}
                onChange={handleRegisterChange}
                margin="normal"
                placeholder="e.g. 9876543210"
                sx={inputSx}
              />
              <Box display={'flex'} justifyContent={'space-between'} alignItems={'center'}>
                {termsCheckbox}
              </Box>
              {!manualSignup && (
                <>
                  <Box mt={2}>{renderGoogleSignup()}</Box>
                  {/* <Typography variant="body2" sx={{ mt: 2, color: '#475467' }}>
                    Prefer email & password?{' '}
                    <Link component="button" type="button" onClick={() => setManualSignup(true)}>
                      Sign up manually
                    </Link>
                  </Typography> */}
                </>
              )}

              {manualSignup && (
                <>
                  <TextField
                    label="Email Address"
                    name="email"
                    fullWidth
                    variant="outlined"
                    value={registerData.email}
                    onChange={handleRegisterChange}
                    margin="normal"
                    sx={inputSx}
                  />
                  <TextField
                    label="Password"
                    name="password"
                    fullWidth
                    variant="outlined"
                    type={showRegisterPassword ? 'text' : 'password'}
                    value={registerData.password}
                    onChange={handleRegisterChange}
                    margin="normal"
                    slotProps={{
                      input: {
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton onClick={() => setShowRegisterPassword(!showRegisterPassword)}>
                              {showRegisterPassword ? <VisibilityOff /> : <Visibility />}
                            </IconButton>
                          </InputAdornment>
                        ),
                      },
                    }}
                    sx={inputSx}
                  />
                  <TextField
                    label="Confirm Password"
                    name="confirmPassword"
                    fullWidth
                    variant="outlined"
                    type={showConfirmPassword ? 'text' : 'password'}
                    value={registerData.confirmPassword}
                    onChange={handleRegisterChange}
                    margin="normal"
                    slotProps={{
                      input: {
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton onClick={() => setShowConfirmPassword(!showConfirmPassword)}>
                              {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                            </IconButton>
                          </InputAdornment>
                        ),
                      },
                    }}
                    sx={inputSx}
                  />
                  <Button type="submit" fullWidth variant="contained" disabled={registering} sx={primaryButtonSx}>
                    {registering ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : 'Register'}
                  </Button>
                </>
              )}
            </form>

            <Typography variant="body2" mt={3} sx={{ color: '#475467' }}>
              Already have an account?{' '}
              <Link component="button" type="button" onClick={goToLogin}>
                Login
              </Link>
            </Typography>
          </>
        )}
      </Paper>
    </Box>
  );
};

export default Login;